import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Stack
} from '@mui/material';
import {
  People,
  Business,
  Assessment,
  Phone,
  Security,
  Chat
} from '@mui/icons-material';
import { useSettings } from '../contexts/SettingsContext';

interface ReportTypeSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectType: (type: 'hourly' | 'work' | 'weekly' | 'user' | 'external' | 'summary' | 'call' | 'risk' | 'livechat') => void;
  userRole?: string;
}

const ReportTypeSelectionModal: React.FC<ReportTypeSelectionModalProps> = ({ 
  open, 
  onClose, 
  onSelectType,
  userRole 
}) => {
  const { settings } = useSettings();
  
  const handleTypeSelect = (type: 'hourly' | 'work' | 'weekly' | 'user' | 'external' | 'summary' | 'call' | 'risk' | 'livechat') => {
    onSelectType(type);
    onClose();
  };

  // Admin kullanıcıları için özel seçenekler
  const isAdmin = userRole === 'admin';
  
  // Kullanıcının rolüne göre aktif rapor türlerini belirle
  const getAvailableReportTypes = () => {
    const availableTypes = [];
    
    // Call raporu - Call Admin, Call, Admin rolleri
    if (['admin', 'call_admin', 'call'].includes(userRole || '') && settings.callReportSettings.enabled) {
      availableTypes.push({
        type: 'call' as const,
        title: 'Call Raporu',
        description: 'Arama merkezi raporu',
        icon: <Phone />,
        color: 'primary.main'
      });
    }
    
    // Risk raporu - Risk Admin, Risk, Admin rolleri
    if (['admin', 'risk_admin', 'risk'].includes(userRole || '') && settings.riskReportSettings.enabled) {
      availableTypes.push({
        type: 'risk' as const,
        title: 'Risk Raporu',
        description: 'Risk yönetimi raporu',
        icon: <Security />,
        color: 'error.main'
      });
    }
    
    // Canlı Chat raporu - LiveChat Admin, LiveChat, Admin rolleri
    if (['admin', 'livechat_admin', 'livechat'].includes(userRole || '') && settings.livechatReportSettings.enabled) {
      availableTypes.push({
        type: 'livechat' as const,
        title: 'Canlı Chat Raporu',
        description: 'Canlı destek raporu',
        icon: <Chat />,
        color: 'success.main'
      });
    }
    
    return availableTypes;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" textAlign="center">
          Rapor Türü Seçin
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
          Oluşturmak istediğiniz rapor türünü seçin
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {isAdmin ? (
            // Admin için özel seçenekler
            <>
              {/* Kullanıcı Raporu */}
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
              >
                <CardActionArea onClick={() => handleTypeSelect('user')}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <People sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                      <Box>
                        <Typography variant="h6" component="h3">
                          Kullanıcı Raporu
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Risk Admin, Call Admin, LiveChat Admin raporlarının özeti
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      • Tüm admin raporlarının özeti<br/>
                      • Kullanıcı performans analizi<br/>
                      • Departman bazlı raporlama<br/>
                      • Genel değerlendirme
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>

              {/* Dış Rapor */}
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
              >
                <CardActionArea onClick={() => handleTypeSelect('external')}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Business sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                      <Box>
                        <Typography variant="h6" component="h3">
                          Dış Rapor
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Marketing, reklam, yazılım geliştirme gibi dış işlerin raporu
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      • Marketing faaliyetleri<br/>
                      • Reklam kampanyaları<br/>
                      • Yazılım geliştirme<br/>
                      • Dış proje takibi
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>

              {/* Haftalık Özet Raporu */}
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
              >
                <CardActionArea onClick={() => handleTypeSelect('summary')}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Assessment sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                      <Box>
                        <Typography variant="h6" component="h3">
                          Haftalık Özet Raporu
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Haftalık raporların genel özeti ve analizi
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      • Haftalık rapor özetleri<br/>
                      • Genel performans analizi<br/>
                      • Trend analizi<br/>
                      • Stratejik değerlendirme
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </>
          ) : (
            // Kullanıcının rolüne göre aktif rapor türleri
            <>
              {getAvailableReportTypes().map((reportType) => (
                <Card 
                  key={reportType.type}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardActionArea onClick={() => handleTypeSelect(reportType.type)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ fontSize: 40, color: reportType.color, mr: 2 }}>
                          {reportType.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" component="h3">
                            {reportType.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {reportType.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        • Rol bazlı raporlama<br/>
                        • Özelleştirilebilir alanlar<br/>
                        • Detaylı analiz<br/>
                        • Performans takibi
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
              
              {/* Eğer hiç aktif rapor türü yoksa */}
              {getAvailableReportTypes().length === 0 && (
                <Card sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    Bu rol için aktif rapor türü bulunmuyor
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Lütfen admin ile iletişime geçin
                  </Typography>
                </Card>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          İptal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportTypeSelectionModal;
